import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Download, Trash2, X, HardDrive, Loader2, CheckCircle,
  AlertCircle, MapPin, ChevronDown, ChevronUp, WifiOff, Tent, Route
} from 'lucide-react';
import { logFeatureUsage } from '../../services/firebaseService';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../services/localeHelpers';
import { MapBounds, OfflineArea, DownloadProgress } from '../../types';
import { MAX_OFFLINE_TILES } from '../../constants';
import {
  estimateTileCount,
  downloadTiles,
  deleteTilesForArea,
  getAllOfflineAreas,
  saveOfflineArea,
  deleteOfflineAreaMetadata,
  getTileStorageEstimateBytes,
  latLngToTile,
  buildTileUrl,
} from '../../services/tileDownloaderService';
import { downloadDataForArea } from '../../services/offlineDataService';
import { getOfflineAreaFreshness } from '../../utils/offlineAreaFreshness';

interface OfflineMapManagerProps {
  currentViewBounds: MapBounds | null;
  onClose: () => void;
  onFlyTo: (bounds: MapBounds) => void;
  isPaidUser: boolean;
  onOpenUpgrade: () => void;
  initialBounds?: MapBounds | null;
  initialLayers?: ('NORWAY_TOPO' | 'SATELLITE')[];
  initialZoomMin?: number;
  initialZoomMax?: number;
  initialIncludeSpots?: boolean;
  initialIncludeRoutes?: boolean;
}

type DetailPreset = 'overview' | 'detailed' | 'street';

const DETAIL_PRESETS: { id: DetailPreset; label: string; desc: string; zoomMin: number; zoomMax: number; previewZoom: number }[] = [
  { id: 'overview', label: 'Oversikt', desc: 'Vegnett og landskap', zoomMin: 10, zoomMax: 12, previewZoom: 11 },
  { id: 'detailed', label: 'Detaljer', desc: 'Stier og bygninger', zoomMin: 10, zoomMax: 14, previewZoom: 13 },
  { id: 'street', label: 'Gatenivå', desc: 'Maksimal detalj', zoomMin: 10, zoomMax: 16, previewZoom: 15 },
];

function derivePreset(zoomMax: number): DetailPreset {
  if (zoomMax <= 12) return 'overview';
  if (zoomMax <= 14) return 'detailed';
  return 'street';
}

function formatBytes(bytes: number): string {
  if (bytes < 1_000_000) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(getDateLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

const OfflineMapManager: React.FC<OfflineMapManagerProps> = ({
  currentViewBounds,
  onClose,
  onFlyTo,
  isPaidUser,
  onOpenUpgrade,
  initialBounds,
  initialLayers,
  initialZoomMin,
  initialZoomMax,
  initialIncludeSpots,
  initialIncludeRoutes,
}) => {
  const { t } = useTranslation();
  const [areas, setAreas] = useState<OfflineArea[]>([]);
  const [areaName, setAreaName] = useState('Mitt kart');
  const [selectedLayers, setSelectedLayers] = useState<('NORWAY_TOPO' | 'SATELLITE')[]>(initialLayers && initialLayers.length > 0 ? initialLayers : ['NORWAY_TOPO']);
  const [zoomMin, setZoomMin] = useState(initialZoomMin ?? 10);
  const [zoomMax, setZoomMax] = useState(initialZoomMax ?? 14);
  const [includeSpots, setIncludeSpots] = useState(initialIncludeSpots ?? true);
  const [includeRoutes, setIncludeRoutes] = useState(initialIncludeRoutes ?? true);
  const [selectedPreset, setSelectedPreset] = useState<DetailPreset>(derivePreset(initialZoomMax ?? 14));
  const [dataStatus, setDataStatus] = useState<string | null>(null);
  const [activeDownloads, setActiveDownloads] = useState<Record<string, DownloadProgress>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [storageBytes, setStorageBytes] = useState(0);
  const [showNewArea, setShowNewArea] = useState(true);
  const abortControllers = useRef<Record<string, AbortController>>({});

  // Last inn lagrede områder
  useEffect(() => {
    setAreas(getAllOfflineAreas());
    getTileStorageEstimateBytes().then(setStorageBytes);
  }, []);

  const refreshStorage = useCallback(async () => {
    setStorageBytes(await getTileStorageEstimateBytes());
  }, []);

  const effectiveBounds = initialBounds ?? currentViewBounds;

  const getPreviewTileUrl = (previewZoom: number): string => {
    if (!effectiveBounds) return '';
    const centerLat = (effectiveBounds.latMin + effectiveBounds.latMax) / 2;
    const centerLng = (effectiveBounds.lonMin + effectiveBounds.lonMax) / 2;
    const { x, y } = latLngToTile(centerLat, centerLng, previewZoom);
    const layer = selectedLayers[0] || 'NORWAY_TOPO';
    return buildTileUrl(layer, previewZoom, x, y);
  };

  const handlePresetSelect = (preset: typeof DETAIL_PRESETS[number]) => {
    setSelectedPreset(preset.id);
    setZoomMin(preset.zoomMin);
    setZoomMax(preset.zoomMax);
  };

  const estimate = effectiveBounds && selectedLayers.length > 0
    ? estimateTileCount(effectiveBounds, zoomMin, zoomMax, selectedLayers)
    : { tileCount: 0, estimatedBytes: 0 };

  const tooLarge = estimate.tileCount > MAX_OFFLINE_TILES;
  const noLayerSelected = selectedLayers.length === 0;
  const canDownload = !tooLarge && !noLayerSelected && effectiveBounds !== null;

  const toggleLayer = (layer: 'NORWAY_TOPO' | 'SATELLITE') => {
    setSelectedLayers((prev) =>
      prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer]
    );
  };

  const handleStartDownload = async () => {
    if (!effectiveBounds || !canDownload) return;
    if (!isPaidUser) { onOpenUpgrade(); return; }

    const area: OfflineArea = {
      id: crypto.randomUUID(),
      name: areaName.trim() || 'Mitt kart',
      createdAt: Date.now(),
      bounds: effectiveBounds,
      layers: selectedLayers,
      zoomMin,
      zoomMax,
      tileCount: estimate.tileCount,
      estimatedBytes: estimate.estimatedBytes,
      status: 'downloading',
    };

    saveOfflineArea(area);
    setAreas((prev) => [area, ...prev]);
    setShowNewArea(false);

    const ac = new AbortController();
    abortControllers.current[area.id] = ac;

    try {
      logFeatureUsage('offline_download');
      await downloadTiles(
        area,
        (progress) => setActiveDownloads((prev) => ({ ...prev, [area.id]: progress })),
        ac.signal
      );

      // Last ned steder/ruter for området
      let spotsCount = 0;
      let routesCount = 0;
      if (includeSpots || includeRoutes) {
        setDataStatus(t('offline.downloading_data'));
        try {
          const dataResult = await downloadDataForArea(
            effectiveBounds,
            { includeSpots, includeRoutes },
            (msg) => setDataStatus(msg)
          );
          spotsCount = dataResult.spotsCount;
          routesCount = dataResult.routesCount;
        } catch {
          // Data-nedlasting feilet — ikke kritisk, tiles er allerede lagret
        }
        setDataStatus(null);
      }

      const completed = {
        ...area,
        status: 'complete' as const,
        includeSpots,
        includeRoutes,
        spotsCount,
        routesCount,
      };
      saveOfflineArea(completed);
      setAreas((prev) => prev.map((a) => (a.id === area.id ? completed : a)));
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        const errored = { ...area, status: 'error' as const };
        saveOfflineArea(errored);
        setAreas((prev) => prev.map((a) => (a.id === area.id ? errored : a)));
      } else {
        // Avbrutt — fjern fra lista
        deleteOfflineAreaMetadata(area.id);
        setAreas((prev) => prev.filter((a) => a.id !== area.id));
      }
    } finally {
      delete abortControllers.current[area.id];
      setActiveDownloads((prev) => {
        const next = { ...prev };
        delete next[area.id];
        return next;
      });
      refreshStorage();
    }
  };

  const handleCancel = (areaId: string) => {
    abortControllers.current[areaId]?.abort();
  };

  const handleDelete = async (area: OfflineArea) => {
    setDeletingId(area.id);
    handleCancel(area.id);
    await deleteTilesForArea(area);
    deleteOfflineAreaMetadata(area.id);
    setAreas((prev) => prev.filter((a) => a.id !== area.id));
    setDeletingId(null);
    refreshStorage();
  };

  return (
    <div className="absolute inset-0 z-drawer flex items-end md:items-start md:justify-end pointer-events-none">
      <div
        className="pointer-events-auto w-full md:w-80 max-h-[85vh] md:max-h-[calc(100vh-120px)] md:mt-4 md:mr-4
          bg-white/95 backdrop-blur-xl border border-stone-200 rounded-t-3xl md:rounded-3xl
          shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 md:slide-in-from-right-2"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-stone-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <WifiOff size={16} className="text-emerald-600" />
            <span className="text-sm font-bold text-stone-800">{t("auto.map.offlinemapmanager.kart_uten_nett", "Kart uten nett")}</span>
          </div>
          <div className="flex items-center gap-3">
            {storageBytes > 0 && (
              <div className="flex items-center gap-1 text-2xs text-[var(--text-secondary)]">
                <HardDrive size={11} />
                <span>{formatBytes(storageBytes)} brukt</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 text-[var(--text-muted)]"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 custom-scrollbar">
          {/* Nytt område-seksjon */}
          <div className="px-5 py-4 border-b border-stone-100">
            <button
              onClick={() => setShowNewArea((v) => !v)}
              className="flex items-center justify-between w-full text-left mb-2"
            >
              <span className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest">
                {t("auto.map.offlinemapmanager.last_ned_nytt_utsnitt", "Last ned nytt utsnitt")}
              </span>
              {showNewArea
                ? <ChevronUp size={14} className="text-[var(--text-secondary)]" />
                : <ChevronDown size={14} className="text-[var(--text-secondary)]" />
              }
            </button>

            {showNewArea && (
              <div className="space-y-3 mt-3">
                {/* Info om valgt område */}
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                  <MapPin size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-tiny text-emerald-800 leading-relaxed">
                    {t("auto.map.offlinemapmanager.kartutsnittet_du_sa_pa_kartet_", "Kartutsnittet du så på kartet vil bli lastet ned for offline-bruk.")}
                  </p>
                </div>

                {/* Navn */}
                <div>
                  <label className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wide block mb-1">
                    {t("auto.map.offlinemapmanager.navn", "Navn")}
                  </label>
                  <input
                    type="text"
                    value={areaName}
                    onChange={(e) => setAreaName(e.target.value)}
                    placeholder={t("auto.map.offlinemapmanager.placeholder.mitt_kart", "Mitt kart")}
                    className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm text-stone-800 bg-stone-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>

                {/* Lag-valg */}
                <div>
                  <label className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wide block mb-1">
                    {t("auto.map.offlinemapmanager.kartlag", "Kartlag")}
                  </label>
                  <div className="flex gap-2">
                    {(['NORWAY_TOPO', 'SATELLITE'] as const).map((layer) => (
                      <button
                        key={layer}
                        onClick={() => toggleLayer(layer)}
                        className={`flex-1 text-tiny font-bold py-2 rounded-xl border transition-all ${
                          selectedLayers.includes(layer)
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-stone-50 text-[var(--text-muted)] border-stone-200 hover:bg-stone-100'
                        }`}
                      >
                        {layer === 'NORWAY_TOPO' ? 'Terrengkart' : 'Flyfoto'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Detaljnivå – visuelt valg */}
                <div>
                  <label className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wide block mb-2">
                    {t("auto.map.offlinemapmanager.detaljniva", "Detaljnivå")}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {DETAIL_PRESETS.map((preset) => {
                      const isSelected = selectedPreset === preset.id;
                      const previewUrl = getPreviewTileUrl(preset.previewZoom);
                      const presetEstimate = effectiveBounds && selectedLayers.length > 0
                        ? estimateTileCount(effectiveBounds, preset.zoomMin, preset.zoomMax, selectedLayers)
                        : null;
                      const presetTooLarge = presetEstimate ? presetEstimate.tileCount > MAX_OFFLINE_TILES : false;

                      return (
                        <button
                          key={preset.id}
                          onClick={() => handlePresetSelect(preset)}
                          className={`rounded-xl border-2 overflow-hidden transition-all ${
                            isSelected
                              ? 'border-emerald-500 ring-2 ring-emerald-200'
                              : presetTooLarge
                                ? 'border-red-200 opacity-60'
                                : 'border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <div
                            className="w-full aspect-square bg-stone-200 bg-cover bg-center"
                            style={previewUrl ? { backgroundImage: `url(${previewUrl})` } : undefined}
                          />
                          <div className="px-1.5 py-2 text-center">
                            <p className={`text-tiny font-bold leading-tight ${isSelected ? 'text-emerald-700' : 'text-stone-700'}`}>
                              {preset.label}
                            </p>
                            <p className="text-3xs text-[var(--text-secondary)] leading-tight mt-0.5">{preset.desc}</p>
                            {presetEstimate && (
                              <p className={`text-3xs font-bold mt-1 ${presetTooLarge ? 'text-red-500' : 'text-[var(--text-secondary)]'}`}>
                                ~{formatBytes(presetEstimate.estimatedBytes)}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Offline data-valg */}
                <div>
                  <label className="text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wide block mb-1">
                    {t('offline.offline_data')}
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIncludeSpots(v => !v)}
                      className={`flex-1 flex items-center justify-center gap-1.5 text-tiny font-bold py-2 rounded-xl border transition-all ${
                        includeSpots
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-stone-50 text-[var(--text-muted)] border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      <Tent size={12} />
                      {t('offline.include_spots')}
                    </button>
                    <button
                      onClick={() => setIncludeRoutes(v => !v)}
                      className={`flex-1 flex items-center justify-center gap-1.5 text-tiny font-bold py-2 rounded-xl border transition-all ${
                        includeRoutes
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-stone-50 text-[var(--text-muted)] border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      <Route size={12} />
                      {t('offline.include_routes')}
                    </button>
                  </div>
                </div>

                {/* Estimat */}
                {currentViewBounds && !noLayerSelected && (
                  <div className={`rounded-xl px-3 py-2 ${tooLarge ? 'bg-red-50 border border-red-200' : 'bg-stone-50 border border-stone-200'}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-tiny font-bold ${tooLarge ? 'text-red-600' : 'text-[var(--text-muted)]'}`}>
                        ~{estimate.tileCount.toLocaleString('nb-NO')} tiles
                      </span>
                      <span className={`text-tiny font-bold ${tooLarge ? 'text-red-600' : 'text-[var(--text-muted)]'}`}>
                        ~{formatBytes(estimate.estimatedBytes)}
                      </span>
                    </div>
                    {tooLarge && (
                      <p className="text-2xs text-red-500 mt-1">
                        {t("auto.map.offlinemapmanager.for_stort_utsnitt_velg_et_mind", "For stort utsnitt. Velg et mindre område på kartet, eller reduser zoom-nivå.")}
                      </p>
                    )}
                  </div>
                )}

                {noLayerSelected && (
                  <p className="text-tiny text-amber-600">{t("auto.map.offlinemapmanager.velg_minst_ett_kartlag", "Velg minst ett kartlag.")}</p>
                )}

                {!currentViewBounds && (
                  <p className="text-tiny text-[var(--text-secondary)]">{t("auto.map.offlinemapmanager.flytt_kartet_til_onsket_omrade", "Flytt kartet til ønsket område.")}</p>
                )}

                <button
                  onClick={handleStartDownload}
                  disabled={!canDownload}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-all ${
                    canDownload
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95'
                      : 'bg-stone-100 text-[var(--text-secondary)] cursor-not-allowed'
                  }`}
                >
                  <Download size={16} />
                  {t("auto.map.offlinemapmanager.last_ned_kartutsnitt", "Last ned kartutsnitt")}
                </button>

                <p className="text-2xs text-[var(--text-secondary)] text-center leading-relaxed">
                  {t("auto.map.offlinemapmanager.nedlastet_kart_bruker_enhetens", "Nedlastet kart bruker enhetens lagringsplass.\r\n                  Begrenset støtte på iOS/Safari.")}
                </p>
              </div>
            )}
          </div>

          {/* Nedlastede områder */}
          <div className="px-5 py-4">
            <p className="text-tiny font-black text-[var(--text-secondary)] uppercase tracking-widest mb-3">
              Nedlastede kart ({areas.length})
            </p>

            {areas.length === 0 ? (
              <div className="text-center py-8">
                <WifiOff size={32} className="text-[var(--text-primary)] mx-auto mb-3" />
                <p className="text-sm font-bold text-[var(--text-secondary)]">{t("auto.map.offlinemapmanager.ingen_nedlastede_kart", "Ingen nedlastede kart")}</p>
                <p className="text-tiny text-[var(--text-secondary)] mt-1 leading-relaxed">
                  {t("auto.map.offlinemapmanager.last_ned_et_kartutsnitt_for_a_", "Last ned et kartutsnitt for å bruke kartet uten nettilgang")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {areas.map((area) => {
                  const progress = activeDownloads[area.id];
                  const isDownloading = area.status === 'downloading' && !!progress;
                  const isDeleting = deletingId === area.id;
                  const pct = isDownloading
                    ? Math.round((progress.downloaded / progress.total) * 100)
                    : 0;

                  return (
                    <div
                      key={area.id}
                      className="border border-stone-200 rounded-2xl p-3 bg-stone-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <MapPin size={11} className="text-emerald-500 flex-shrink-0" />
                            <span className="text-[12px] font-bold text-stone-800 truncate">
                              {area.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-2xs text-[var(--text-secondary)] flex-wrap">
                            <span>Zoom {area.zoomMin}–{area.zoomMax}</span>
                            <span>·</span>
                            <span>{area.tileCount.toLocaleString('nb-NO')} tiles</span>
                            <span>·</span>
                            <span>~{formatBytes(area.estimatedBytes)}</span>
                          </div>
                          <div className="text-2xs text-[var(--text-secondary)] mt-0.5">
                            {area.layers.map((l) => l === 'NORWAY_TOPO' ? 'Terrengkart' : 'Flyfoto').join(', ')}
                            {area.spotsCount ? ` · ${area.spotsCount} steder` : ''}
                            {area.routesCount ? ` · ${area.routesCount} ruter` : ''}
                            {area.status === 'complete' && ` · ${formatDate(area.createdAt)}`}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* Status badge */}
                          {area.status === 'complete' && (
                            <CheckCircle size={16} className="text-emerald-500" />
                          )}
                          {area.status === 'error' && (
                            <AlertCircle size={16} className="text-red-400" />
                          )}

                          {/* Gå til */}
                          {area.status === 'complete' && (
                            <button
                              onClick={() => { onFlyTo(area.bounds); onClose(); }}
                              className="text-2xs font-bold text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50"
                            >
                              {t("auto.map.offlinemapmanager.ga_til", "Gå til")}
                            </button>
                          )}

                          {/* Slett / avbryt */}
                          {isDownloading ? (
                            <button
                              onClick={() => handleCancel(area.id)}
                              className="text-2xs font-bold text-[var(--text-muted)] hover:text-red-500 px-2 py-1 rounded-lg hover:bg-red-50"
                            >
                              {t("auto.map.offlinemapmanager.avbryt", "Avbryt")}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDelete(area)}
                              disabled={isDeleting}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[var(--text-secondary)] hover:text-red-500 disabled:opacity-40"
                            >
                              {isDeleting
                                ? <Loader2 size={14} className="animate-spin" />
                                : <Trash2 size={14} />
                              }
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Ferskhet-varsel (#6B): ærlig påminnelse når offline-kartet eldes.
                          Holder ingenting som gissel — påpeker bare at dataene kan være utdaterte. */}
                      {area.status === 'complete' && (() => {
                        const fresh = getOfflineAreaFreshness(area.createdAt, Date.now());
                        if (!fresh.isStale) return null;
                        const veryStale = fresh.level === 'very_stale';
                        return (
                          <div className={`mt-2 flex items-start gap-1.5 rounded-xl px-2.5 py-1.5 text-2xs font-semibold ${veryStale ? 'bg-amber-100 text-amber-800' : 'bg-amber-50 text-amber-700'}`}>
                            <AlertCircle size={12} className="flex-shrink-0 mt-px" />
                            <span>
                              {t('auto.map.offlinemapmanager.stale_hint', 'Lastet ned for {{count}} dager siden — kartdata kan ha endret seg. Slett og last ned på nytt for ferske kart.', { count: fresh.ageDays })}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Progress bar */}
                      {isDownloading && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-2xs text-[var(--text-secondary)] mb-1">
                            <div className="flex items-center gap-1">
                              <Loader2 size={10} className="animate-spin text-emerald-500" />
                              <span>{t("auto.map.offlinemapmanager.laster_ned", "Laster ned...")}</span>
                            </div>
                            <span>{progress.downloaded.toLocaleString('nb-NO')} / {progress.total.toLocaleString('nb-NO')}</span>
                          </div>
                          <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {isDownloading && dataStatus && (
                        <div className="flex items-center gap-1 mt-1 text-2xs text-emerald-600">
                          <Loader2 size={10} className="animate-spin" />
                          <span>{dataStatus}</span>
                        </div>
                      )}

                      {area.status === 'error' && (
                        <p className="text-2xs text-red-400 mt-1.5">
                          {t("auto.map.offlinemapmanager.nedlasting_feilet_slett_og_pro", "Nedlasting feilet. Slett og prøv igjen.")}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineMapManager;
